import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createExportAPI, downloadExportAPI, getExportHistoryAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';

export default function ExportHistory() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'users_csv', format: 'CSV', from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getExportHistoryAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { name: 'Export', selector: (r) => r.name },
      { name: 'Requested by', selector: (r) => r.requestedBy },
      { name: 'Date', selector: (r) => new Date(r.createdAt).toLocaleString() },
      { name: 'Format', selector: (r) => r.format },
      {
        name: 'Status',
        selector: (r) => r.status,
      },
      {
        name: 'Download',
        cell: (r) =>
          r.status === 'ready' && r.downloadUrl ? (
            <Button size="sm" variant="link" onClick={() => downloadExportAPI(r.id).then(() => toast.success('Ready (mock)'))}>
              Download
            </Button>
          ) : r.status === 'expired' ? (
            <Button size="sm" variant="outline-secondary" onClick={() => toast.success('Regenerating (mock)')}>
              Regenerate
            </Button>
          ) : (
            <span className="text-muted small">—</span>
          ),
      },
    ],
    []
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Export history" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Audit', path: companyUrl('audit') }]} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Export history"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Audit', path: companyUrl('audit') },
          { label: 'Exports' },
        ]}
        primaryAction={{ label: 'New Export', onClick: () => setModal(true) }}
      />
      {loading && !rows.length ? (
        <LoadingSkeleton rows={4} cols={4} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch={false} />
      )}

      <Modal show={modal} onHide={() => setModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>New export</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Name</Form.Label>
            <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Type</Form.Label>
            <Form.Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="users_csv">Users</option>
              <option value="audit_pack">Audit pack</option>
              <option value="transcripts_zip">Transcripts</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Format</Form.Label>
            <Form.Select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
              <option>CSV</option>
              <option>XLSX</option>
            </Form.Select>
          </Form.Group>
          <Row className="g-2">
            <Col>
              <Form.Label>From</Form.Label>
              <Form.Control type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
            </Col>
            <Col>
              <Form.Label>To</Form.Label>
              <Form.Control type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
          <Button
            onClick={() =>
              createExportAPI(form).then(() => {
                toast.success('Export requested');
                setModal(false);
                load();
              })
            }
          >
            Start
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
